// @refresh reset
import { useLocation } from "react-router-dom";
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { env } from "~/env";

import {
    clearTokens,
    getAccessToken,
    getValidAccessToken,
    logout as authLogout,
    parseJwtPayload,
} from "~/shared/lib/auth/auth-client";

interface Session {
    user: {
        id: string;
        email?: string | null;
        name?: string | null;
        image?: string | null;
    };
    expires: string;
}

type SessionStatus = "loading" | "authenticated" | "unauthenticated";

interface SessionContextValue {
    data: Session | null;
    status: SessionStatus;
    update: () => Promise<void>;
}

const defaultUpdate = async () => {
    /* noop */
};

const SessionContext = createContext<SessionContextValue>({
    data: null,
    status: "loading",
    update: defaultUpdate,
});

export function SessionProvider({ children }: { children: ReactNode }) {
    const _location = useLocation();
    const [session, setSession] = useState<Session | null>(null);
    const [status, setStatus] = useState<SessionStatus>("loading");

    const loadSession = useCallback(async () => {
        const token = await getValidAccessToken();
        if (!token) {
            setSession(null);
            setStatus("unauthenticated");
            return;
        }

        const payload = parseJwtPayload(token);
        if (!payload?.sub) {
            clearTokens();
            setSession(null);
            setStatus("unauthenticated");
            return;
        }

        setSession({
            user: {
                id: payload.sub as string,
                email: (payload.email as string) ?? null,
                name: null,
                image: null,
            },
            expires: new Date(((payload.exp as number) ?? 0) * 1000).toISOString(),
        });
        setStatus("authenticated");
    }, []);

    useEffect(() => {
        void loadSession();
    }, [loadSession]);

    // Check user status on every page navigation
    // Check user status once on auth, not on every navigation
    useEffect(() => {
        if (status !== "authenticated") return;

        const checkUserStatus = async () => {
            const validToken = await getValidAccessToken();
            if (!validToken) {
                // Only redirect if tokens are actually gone (not just a network error)
                if (!getAccessToken()) {
                    window.location.href = "/";
                }
                return;
            }

            try {
                const res = await fetch(`${env.VITE_API_URL}/v2/users/me`, {
                    headers: { Authorization: `Bearer ${validToken}` },
                });
                if (res.status === 403) {
                    // User blocked/deleted
                    clearTokens();
                    window.location.href = "/";
                }
                // 401 won't happen — getValidAccessToken already refreshed
            } catch {
                /* ignore network errors */
            }
        };

        void checkUserStatus();
    }, [status]);

    // Listen for storage events (login/logout in other tabs)
    useEffect(() => {
        const handler = (e: StorageEvent) => {
            if (e.key === "auth_access_token") {
                void loadSession();
            }
        };
        window.addEventListener("storage", handler);
        return () => window.removeEventListener("storage", handler);
    }, [loadSession]);

    // Track user activity every 15 minutes
    useEffect(() => {
        if (status !== "authenticated") return;

        const trackAction = async () => {
            const token = await getValidAccessToken();
            if (!token) return;
            try {
                await fetch(`${env.VITE_API_URL}/v2/users/track-action`, {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${token}`,
                    },
                });
            } catch {
                /* ignore */
            }
        };

        void trackAction();
        const interval = setInterval(() => void trackAction(), 15 * 60 * 1000);
        return () => clearInterval(interval);
    }, [status]);

    const value = useMemo(
        () => ({ data: session, status, update: loadSession }),
        [session, status, loadSession],
    );

    return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession() {
    return useContext(SessionContext);
}

export async function signOut(options?: { callbackUrl?: string; redirect?: boolean }) {
    await authLogout();

    // Clear all client-side state (cookies, storage) — same as /auth/cleanup
    const secure = window.location.protocol === "https:" ? "; Secure" : "";
    for (const cookie of document.cookie.split(";")) {
        const name = cookie.split("=")[0]?.trim();
        if (name) {
            document.cookie = `${name}=; path=/; max-age=0; SameSite=Lax${secure}`;
            document.cookie = `${name}=; path=/; max-age=0; SameSite=Lax`;
        }
    }
    localStorage.clear();
    sessionStorage.clear();

    if (options?.redirect !== false) {
        window.location.href = options?.callbackUrl ?? "/";
    }
}
